import { sdk } from '@/lib/sdk'
import type { Call4PapersCallDraft } from '@/types/space-wizard'
import type { CallDetail } from '@/types/call4papers'

/**
 * The bridge between the form draft and the body the API accepts.
 *
 * It lives outside the component because the two paths that save a call — the
 * listing dialog and the detail page — need exactly the same conversion.
 * Duplicating it would have one screen save a field the other forgets.
 */

/** Date + time → floating datetime, like the rest of the house. No timezone. */
const joinDateTime = (date: string, time: string): string | null =>
  date ? `${date}T${time || '00:00'}:00.000` : null

/**
 * A new track has a local key (`t1727…`), an existing one has the server id.
 * Sending the local key as `id` would make the server look for a record that
 * does not exist and create a second track on every save.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * An image just picked is still a browser `data:` URL — the file only uploads
 * afterwards, once the call has an id to build the S3 key from. Sending it
 * here would store the whole image as base64 inside the column.
 *
 * With a file pending the field is **omitted**, not cleared: clearing would
 * wipe the current image in the window between the `PUT` and the upload, and a
 * failed upload would leave the call without the cover it already had.
 */
const persistedImageUrl = (
  url: string | null | undefined,
  pendingFile: File | null | undefined,
): string | null | undefined => {
  if (pendingFile) return undefined
  if (url && /^(data|blob):/i.test(url)) return undefined
  return url ?? null
}

/** O corpo de `POST /call4papers` e `PUT /call4papers/{id}`. */
export const callPayloadFromDraft = (
  draft: Call4PapersCallDraft,
  callType: 'simple' | 'advanced',
): Record<string, any> => ({
  type: callType,
  title: draft.title,
  description: draft.description,
  modality: draft.modality,
  bannerUrl: persistedImageUrl(draft.bannerUrl, draft.bannerFile),
  featuredUrl: persistedImageUrl(draft.featuredUrl, draft.featuredFile),

  submissionOpensAt: joinDateTime(draft.submissionOpensDate, draft.submissionOpensTime),
  submissionClosesAt: joinDateTime(draft.submissionClosesDate, draft.submissionClosesTime),
  /* Só quando há avaliação.
   *
   * O formulário esconde a janela em `evaluationMode: 'none'`, mas a sugestão
   * automática — "abre um minuto depois que a submissão fecha" — preenche o
   * rascunho de qualquer jeito. O resultado era uma chamada sem avaliação
   * guardando período de avaliação, e a tela de detalhe mostrando "Período de
   * avaliação" logo abaixo da frase que diz que não há comitê nem votação.
   *
   * `null` e não `undefined`: quem trocar uma chamada de `committee` para
   * `none` precisa que as datas antigas SAIAM da coluna. `undefined` deixaria
   * como estava. */
  reviewOpensAt: draft.evaluationMode === 'none'
    ? null
    : joinDateTime(draft.reviewOpensDate, draft.reviewOpensTime),
  reviewClosesAt: draft.evaluationMode === 'none'
    ? null
    : joinDateTime(draft.reviewClosesDate, draft.reviewClosesTime),

  allowedFormats: draft.allowedFormats,
  allowedDurations: draft.allowedDurations,
  speakerBenefits: draft.speakerBenefits,

  /* Tracks and dates go as a block: the server reconciles (keeps what has an
     id, creates what does not, deletes what vanished) in a single transaction. */
  tracks: draft.tracks
    .filter((track) => track.name.trim())
    .map((track) => ({
      ...(UUID.test(track.key) ? { id: track.key } : {}),
      name: track.name,
      purpose: track.purpose,
    })),
  eventDates: draft.eventDates
    .filter((d) => d.date)
    .map(({ date, startTime, endTime }) => ({ date, startTime, endTime })),

  evaluationMode: draft.evaluationMode,
  /* The voting window is the same review period: with a committee it says when
     scoring happens, with the community when voting does. One field on screen,
     two columns in the database — the mode decides which one gets filled. */
  votingOpensAt: draft.evaluationMode === 'community'
    ? joinDateTime(draft.reviewOpensDate, draft.reviewOpensTime)
    : null,
  votingClosesAt: draft.evaluationMode === 'community'
    ? joinDateTime(draft.reviewClosesDate, draft.reviewClosesTime)
    : null,
  minReviewsPerSubmission: draft.minReviewsPerSubmission,
  blindReview: draft.blindReview,
  allowCounterOffer: draft.allowCounterOffer,

  airTravelCost: draft.airTravelCost,
  groundTransportCost: draft.groundTransportCost,
  parkingCost: draft.parkingCost,
  accommodationCost: draft.accommodationCost,
  mealCost: draft.mealCost,
  speakerFeeMode: draft.speakerFeeMode,
  /* A moeda é da comunidade e quem a grava é o servidor — mandá-la daqui
     deixaria um edital em real dentro de uma comunidade portuguesa. */
  speakerFeeAmount: draft.speakerFeeMode === 'none' ? null : draft.speakerFeeAmount,
})

/**
 * Settles the owner list after the call is saved.
 *
 * Owners do not travel in the `POST`/`PUT` body: each one is an invitation with
 * its own state, and the API has a route for that. The creator is born an
 * accepted owner, so only what the form added or removed becomes a request.
 *
 * A failure here does not bring the save down — the call is already stored, and
 * losing the whole screen over an invitation would be worse than the missing
 * invitation. The error surfaces in the return for the caller to decide.
 */
const syncOwners = async (
  callId: string,
  wantedMemberIds: string[],
): Promise<void> => {
  let current: any[] = []
  try {
    const result = await sdk.call4papers.listOwners(callId)
    current = Array.isArray(result) ? result : (result?.items ?? result?.owners ?? [])
  } catch {
    /* With no current list there is no difference to compute — and inventing
       one would re-invite somebody who already owns the call. */
    return
  }

  const currentIds = new Set(
    current.map((o: any) => o.member?.id ?? o.memberId).filter(Boolean),
  )
  const wanted = new Set(wantedMemberIds)

  await Promise.all([
    ...wantedMemberIds
      .filter((id) => !currentIds.has(id))
      .map((id) => sdk.call4papers.addOwner(callId, id).catch(() => undefined)),
    ...current
      .filter((o: any) => {
        const memberId = o.member?.id ?? o.memberId
        return memberId && !wanted.has(memberId)
      })
      .map((o: any) => sdk.call4papers.removeOwner(o.id).catch(() => undefined)),
  ])
}

/**
 * Gives each track the committee the form shows. Returns whether anything
 * changed.
 *
 * After the save because the link is **per track**, and a track created just
 * now only gets an id in the server's answer. Matching is by id when the track
 * already existed and by name when it was just born — the same pair the server
 * used to reconcile.
 *
 * There is no invitation to accept: whoever lands here reviews already. The
 * route stamps the acceptance at the same moment.
 */
const syncTrackReviewers = async (
  saved: CallDetail,
  draft: Call4PapersCallDraft,
): Promise<boolean> => {
  const links = saved.trackReviewers ?? []
  const jobs: Promise<unknown>[] = []

  for (const track of draft.tracks) {
    const server =
      saved.tracks.find((t) => t.id === track.key) ??
      saved.tracks.find((t) => t.name.trim() === track.name.trim())
    if (!server) continue

    const current = links.filter((link) => link.trackId === server.id)
    const currentIds = new Set(current.map((link) => link.member.id))
    const wanted = new Set(track.reviewers.map((p) => p.id).filter(Boolean))

    for (const id of wanted) {
      if (!currentIds.has(id)) {
        jobs.push(sdk.call4papers.addTrackReviewer(server.id, id).catch(() => undefined))
      }
    }
    for (const link of current) {
      if (!wanted.has(link.member.id)) {
        jobs.push(sdk.call4papers.removeTrackReviewer(link.id).catch(() => undefined))
      }
    }
  }

  if (jobs.length === 0) return false
  await Promise.all(jobs)
  return true
}

/**
 * Uploads the images picked in the form. Returns whether any went up.
 *
 * After the save out of necessity: the S3 key carries the call id, and on a
 * `POST` that id only exists once the server answers. The upload route is what
 * writes the column — which is why the detail in hand goes stale and has to be
 * read again.
 */
const uploadCallImages = async (
  callId: string,
  draft: Call4PapersCallDraft,
): Promise<boolean> => {
  const jobs: Promise<unknown>[] = []
  if (draft.bannerFile) {
    jobs.push(sdk.upload.uploadCallForPapersBanner(draft.bannerFile, callId))
  }
  if (draft.featuredFile) {
    jobs.push(sdk.upload.uploadCallForPapersFeatured(draft.featuredFile, callId))
  }
  if (jobs.length === 0) return false
  await Promise.all(jobs)
  return true
}

/**
 * Creates or updates a call and returns the detail the server stored.
 *
 * Returning what the API answered, rather than the draft laid over what was
 * there, is what stops the screen from showing a version the server partly
 * refused — a normalized slug, a filled currency, a track with a new id.
 */
export const saveCall = async (args: {
  id?: string
  spaceId?: string
  draft: Call4PapersCallDraft
  callType: 'simple' | 'advanced'
  before?: CallDetail | null
}): Promise<CallDetail> => {
  const { id, spaceId, draft, callType, before = null } = args
  const payload = callPayloadFromDraft(draft, callType)

  const saved: CallDetail = id
    ? await sdk.call4papers.update(id, payload)
    : await sdk.call4papers.create({ ...payload, spaceId })

  const wantedOwners = draft.owners.map((p) => p.id).filter(Boolean)
  const touchedOwners = wantedOwners.length > 0 || (before?.owners?.length ?? 0) > 0
  if (touchedOwners) await syncOwners(saved.id, wantedOwners)

  const uploadedImages = await uploadCallImages(saved.id, draft)
  const touchedReviewers = callType === 'advanced'
    ? await syncTrackReviewers(saved, draft)
    : false

  /* Owners, reviewers and images are written **after** the POST/PUT, each by
     its own route. Only then is the detail in hand stale — one extra read on
     every save would be a request paid for nothing. */
  if (touchedOwners || uploadedImages || touchedReviewers) {
    try {
      return await sdk.call4papers.get(saved.id)
    } catch {
      return saved
    }
  }

  return saved
}
